import { alpha, type Components, type Theme } from '@mui/material/styles'

import { statusTone, type StatusToneKind, statusToneStyles } from '@/themes/statusTone'
import { borderRadius } from '@/themes/tokens'

// Extend MenuItem to accept custom variants
declare module '@mui/material/MenuItem' {
  interface MenuItemOwnProps {
    variant?: 'destructiveAction' | 'restorativeAction'
  }
}

/**
 * A menu item that is an action rather than a choice, coloured for the direction it
 * goes: red away from access, green back towards it.
 *
 * Wears the same tone as the status chip it sits beside, because the action is the
 * inverse of the chip and naming the two colours twice would be two vocabularies
 * for one idea.
 */
const actionVariant = (theme: Theme, kind: StatusToneKind) => ({
  ...statusToneStyles(theme, kind),
  // The same curve the surface is clipped to. A square corner here would be
  // sliced off by that clip, and the border with it.
  borderRadius: borderRadius.chip,
  '&:hover': {
    backgroundColor: alpha(
      statusTone(theme, kind).base,
      theme.palette.mode === 'dark' ? 0.2 : 0.18
    ),
  },
})

export const MuiMenuItemOverride: Components<Theme>['MuiMenuItem'] = {
  variants: [
    {
      props: { variant: 'destructiveAction' },
      style: ({ theme }) => actionVariant(theme, 'negative'),
    },
    {
      props: { variant: 'restorativeAction' },
      style: ({ theme }) => actionVariant(theme, 'positive'),
    },
  ],
}
